import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { EmptyState } from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Users, Briefcase, Zap, Check, X, PlugZap, ShieldCheck, Mail, Download, RefreshCw, Sparkles, Trash2, Plus, Pencil, Ban, LayoutGrid, List, MapPin } from "lucide-react";
import { AdminAdsTab } from "../components/AdminAdsTab";
import { AdminApiKeysTab } from "../components/AdminApiKeysTab";
import { AdminLegalTab } from "../components/AdminLegalTab";
import { AdminSidebar } from "../components/admin/AdminSidebar";
import { AdminOverviewSection } from "../components/admin/AdminOverviewSection";
import { AdminUsersSection } from "../components/admin/AdminUsersSection";
import { AdminAggregatorsSection } from "../components/admin/AdminAggregatorsSection";
import { AdminUserProfileSection } from "../components/admin/AdminUserProfileSection";
import { AdminCompanyProfileSection } from "../components/admin/AdminCompanyProfileSection";
import { AdminJobDetailSection } from "../components/admin/AdminJobDetailSection";
import { AdminApplicationsSection } from "../components/admin/AdminApplicationsSection";
import { AdminJobFormDialog } from "../components/admin/AdminJobFormDialog";
import { AiModerationBadge, AdminModerationBanner } from "../components/admin/AdminAiModeration";
import { AdminGoogleJobsCard } from "../components/admin/AdminGoogleJobsCard";
import { JobCoverImage } from "../components/JobCoverImage";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const JOB_STATUS = { pending: ["Pendente", "bg-amber-100 text-amber-700"], active: ["Ativa", "bg-emerald-100 text-emerald-700"], rejected: ["Rejeitada", "bg-red-100 text-red-700"], closed: ["Inativa", "bg-slate-200 text-slate-600"] };

function PendingFilterChip({ active, count, onToggle, testId }) {
  if (!count && !active) return null;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onToggle}
      className={`mb-4 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
        active
          ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
          : "bg-white text-slate-700 border-black/10 hover:border-amber-400 hover:text-amber-700"
      }`}
    >
      {active ? "A mostrar só pendentes" : `Só pendentes (${count})`}
      {active && <X className="w-3.5 h-3.5" />}
    </button>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [emailLog, setEmailLog] = useState({ emails: [], resend_configured: true });
  const [importData, setImportData] = useState({ runs: [], iefp_jobs_total: 0 });
  const [importing, setImporting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [seedResetOpen, setSeedResetOpen] = useState(false);
  const [onlyPending, setOnlyPending] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [jobStatusFilter, setJobStatusFilter] = useState("all");
  const [jobFormOpen, setJobFormOpen] = useState(false);
  const [jobEditData, setJobEditData] = useState(null);
  const [jobsView, setJobsView] = useState(() => {
    try { return localStorage.getItem("f2j_admin_jobs_view") || "grid"; } catch { return "grid"; }
  });
  const [jobsSearch, setJobsSearch] = useState("");
  const [debouncedJobsSearch, setDebouncedJobsSearch] = useState("");
  const [aiFilter, setAiFilter] = useState("");
  const [jobCounts, setJobCounts] = useState({});
  const [jobsTotal, setJobsTotal] = useState(0);
  const [jobsHasMore, setJobsHasMore] = useState(false);
  const [jobsLoadingMore, setJobsLoadingMore] = useState(false);
  const [companiesSearch, setCompaniesSearch] = useState("");

  useEffect(() => {
    try { localStorage.setItem("f2j_admin_jobs_view", jobsView); } catch { /* ignore */ }
  }, [jobsView]);

  // Estado da secção via URL query (?s=overview)
  const params = new URLSearchParams(location.search);
  const section = params.get("s") || "overview";

  const navigateTo = useCallback((s, extra = {}) => {
    const p = new URLSearchParams();
    p.set("s", s);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    navigate(`/admin?${p.toString()}`);
  }, [navigate]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedJobsSearch(jobsSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [jobsSearch]);

  // Filtros de vagas aplicados no servidor (escala para dezenas de milhares de vagas)
  const jobsParams = useCallback((offset = 0) => {
    const p = { limit: 120, offset };
    if (jobStatusFilter !== "all") p.status = jobStatusFilter;
    if (aiFilter) p.ai = aiFilter;
    if (debouncedJobsSearch) p.q = debouncedJobsSearch;
    return p;
  }, [jobStatusFilter, aiFilter, debouncedJobsSearch]);

  const loadJobs = useCallback(() => {
    api.get("/admin/jobs", { params: jobsParams(0) }).then((r) => {
      setJobs(r.data.jobs); setJobsTotal(r.data.total ?? r.data.jobs.length); setJobsHasMore(!!r.data.has_more);
    }).catch(() => {});
    api.get("/admin/stats").then((r) => setJobCounts(r.data)).catch(() => {});
  }, [jobsParams]);

  const loadMoreJobs = async () => {
    setJobsLoadingMore(true);
    try {
      const r = await api.get("/admin/jobs", { params: jobsParams(jobs.length) });
      setJobs((prev) => [...prev, ...r.data.jobs]); setJobsHasMore(!!r.data.has_more);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setJobsLoadingMore(false); }
  };

  useEffect(() => { if (user && user.role === "admin") loadJobs(); }, [user, loadJobs]);

  const refresh = useCallback(() => {
    loadJobs();
    api.get("/admin/companies").then((r) => setCompanies(r.data.companies)).catch(() => {});
    api.get("/admin/boosts").then((r) => setBoosts(r.data.boosts)).catch(() => {});
    api.get("/admin/emails").then((r) => setEmailLog(r.data)).catch(() => {});
    api.get("/admin/import/runs").then((r) => setImportData(r.data)).catch(() => {});
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, [loadJobs]);

  useEffect(() => {
    if (user === false) navigate("/auth");
    if (user && user.role !== "admin") navigate("/");
    if (user && user.role === "admin") refresh();
  }, [user, navigate, refresh]);

  // Sync selectedUserId da query ?u=
  useEffect(() => {
    setSelectedUserId(params.get("u") || null);
    setSelectedCompanyId(params.get("c") || null);
    setSelectedJobId(params.get("j") || null);
  }, [location.search]);

  if (!user || user.role !== "admin") return <div className="min-h-screen bg-[#FAFAFA]"><Navbar /></div>;

  const pendingJobsList = jobs.filter((j) => j.status === "pending");
  const pendingCompaniesList = companies.filter((c) => c.status === "pending");
  const pendingJobsCount = jobCounts.pending_jobs ?? pendingJobsList.length;
  const pendingCompaniesCount = pendingCompaniesList.length;
  const companiesShown = (onlyPending ? pendingCompaniesList : companies).filter((c) => {
    if (!companiesSearch) return true;
    const q = companiesSearch.toLowerCase();
    return (c.company_name || "").toLowerCase().includes(q)
      || (c.district || "").toLowerCase().includes(q);
  });
  const jobsFiltered = jobs.filter((j) => {
    if (!jobsSearch) return true;
    const q = jobsSearch.toLowerCase();
    return (j.title || "").toLowerCase().includes(q)
      || (j.company_name || "").toLowerCase().includes(q)
      || (j.concelho || "").toLowerCase().includes(q)
      || (j.district || "").toLowerCase().includes(q);
  });

  const moderate = async (type, id, action) => {
    try {
      await api.patch(`/admin/${type}/${id}`, { action });
      toast.success(action === "approve" ? "Aprovado" : "Rejeitado");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openCreateJob = () => { setJobEditData(null); setJobFormOpen(true); };
  const openEditJob = (job) => { setJobEditData(job); setJobFormOpen(true); };
  const deactivateJob = async (job) => {
    try {
      await api.delete(`/admin/jobs/${job.id}`);
      toast.success("Vaga desativada");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const deleteJob = async (job) => {
    if (!window.confirm(`Apagar definitivamente a vaga "${job.title}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/jobs/${job.id}?hard=true`);
      toast.success("Vaga apagada");
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      await api.post("/admin/import/iefp", null, { params: { limit: 5000 } });
      toast.success("Importação IEFP iniciada (até 5.000 vagas) — demora alguns minutos.");
      setTimeout(() => { refresh(); setImporting(false); }, 12000);
    } catch (e) {
      toast.error(formatErr(e));
      setImporting(false);
    }
  };

  const runSeed = async (reset = false) => {
    setSeeding(true);
    setSeedResetOpen(false);
    try {
      const { data } = await api.post("/admin/seed-demo", { reset });
      setSeedResult(data);
      if (data.created && Object.keys(data.created).length > 0) {
        toast.success("Dados de demonstração criados com sucesso!");
      } else {
        toast.info("Dados de demonstração já existiam. Usa 'Repor' para recriar.");
      }
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSeeding(false); }
  };

  const runPurgeContent = async () => {
    if (!window.confirm("Isto apaga TODOS os anúncios/vagas demo, banners, candidaturas, favoritos, reviews, alertas e histórico Stripe. As contas de utilizador e empresa demo são mantidas. Continuar?")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/purge-demo-content", { keep_accounts: true });
      const items = Object.entries(data.deleted || {}).map(([k, v]) => `${k}: ${v}`).join(", ") || "nada para apagar";
      toast.success(`Conteúdos demo removidos (${items}). Contas preservadas.`);
      setSeedResult(null);
      refresh();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSeeding(false); }
  };

  const openUserProfile = (uid) => {
    const p = new URLSearchParams();
    p.set("s", "users");
    p.set("u", uid);
    navigate(`/admin?${p.toString()}`);
  };

  const closeUserProfile = () => {
    const p = new URLSearchParams();
    p.set("s", "users");
    navigate(`/admin?${p.toString()}`);
  };

  const openCompanyProfile = (cid) => {
    const p = new URLSearchParams();
    p.set("s", "companies");
    p.set("c", cid);
    navigate(`/admin?${p.toString()}`);
  };

  const closeCompanyProfile = () => {
    const p = new URLSearchParams();
    p.set("s", "companies");
    navigate(`/admin?${p.toString()}`);
  };

  const openJobDetail = (jid) => {
    const p = new URLSearchParams();
    p.set("s", "moderation");
    p.set("j", jid);
    navigate(`/admin?${p.toString()}`);
  };

  const closeJobDetail = () => {
    const p = new URLSearchParams();
    p.set("s", "moderation");
    navigate(`/admin?${p.toString()}`);
  };

  const renderSection = () => {
    if (section === "overview") {
      return (
        <AdminOverviewSection
          pendingJobs={pendingJobsCount}
          pendingCompanies={pendingCompaniesCount}
          onGoJobs={() => { setOnlyPending(true); navigateTo("moderation"); }}
          onGoCompanies={() => { setOnlyPending(true); navigateTo("companies"); }}
          onNavigate={(s) => navigateTo(s)}
        />
      );
    }
    if (section === "users") {
      if (selectedUserId) {
        return <AdminUserProfileSection userId={selectedUserId} onBack={closeUserProfile} />;
      }
      return <AdminUsersSection onOpenUser={openUserProfile} />;
    }
    if (section === "moderation") {
      if (selectedJobId) {
        return <AdminJobDetailSection jobId={selectedJobId} onBack={closeJobDetail} onOpenCompany={openCompanyProfile} onOpenApplication={openUserProfile} />;
      }
      return (
        <div>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-heading font-black tracking-tighter text-2xl text-slate-900">Vagas — gestão e moderação</h2>
              <p className="text-sm text-slate-500">Cria, edita e modera vagas. Clica numa linha para ver detalhes e candidatos.</p>
            </div>
            <Button data-testid="admin-new-job-btn" className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={openCreateJob}>
              <Plus className="w-4 h-4 mr-1.5" /> Nova vaga
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { id: "all", label: `Todas (${(jobCounts.total_jobs ?? 0).toLocaleString("pt-PT")})` },
              { id: "active", label: `Ativas (${(jobCounts.active_jobs ?? 0).toLocaleString("pt-PT")})` },
              { id: "pending", label: `Pendentes (${(jobCounts.pending_jobs ?? 0).toLocaleString("pt-PT")})` },
              { id: "inactive", label: `Expiradas/Inativas (${(jobCounts.inactive_jobs ?? 0).toLocaleString("pt-PT")})` },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`job-filter-${t.id}`}
                onClick={() => setJobStatusFilter(t.id)}
                className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${
                  jobStatusFilter === t.id
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white text-slate-700 border-black/10 hover:border-amber-400 hover:text-amber-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mb-4" data-testid="ai-filter-chips">
            {[
              { id: "", label: "IA: todas" },
              { id: "flagged", label: `Sinalizadas pela IA (${jobCounts.ai_flagged_jobs ?? 0})` },
              { id: "malicious", label: "Maliciosas" },
              { id: "queued", label: `Em análise (${jobCounts.ai_queued_jobs ?? 0})` },
            ].map((t) => (
              <button key={t.id || "all"} type="button" data-testid={`ai-filter-${t.id || "all"}`}
                onClick={() => setAiFilter(t.id)}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold border transition-colors ${
                  aiFilter === t.id ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-black/10 hover:border-violet-400 hover:text-violet-700"}`}>
                <Sparkles className="w-3 h-3" /> {t.label}
              </button>
            ))}
          </div>
          <AdminModerationBanner onChanged={refresh} />

          {/* Search + View toggle */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <input
                type="search"
                data-testid="admin-jobs-search"
                value={jobsSearch}
                onChange={(e) => setJobsSearch(e.target.value)}
                placeholder="Procurar por título, empresa, concelho…"
                className="w-full h-10 rounded-full border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400"
              />
            </div>
            <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm" role="group" aria-label="Modo de visualização">
              <button type="button" data-testid="admin-jobs-view-list"
                aria-pressed={jobsView === "list"}
                onClick={() => setJobsView("list")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${jobsView === "list" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
                <List className="w-3.5 h-3.5" /> Lista
              </button>
              <button type="button" data-testid="admin-jobs-view-grid"
                aria-pressed={jobsView === "grid"}
                onClick={() => setJobsView("grid")}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${jobsView === "grid" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
                <LayoutGrid className="w-3.5 h-3.5" /> Grelha
              </button>
            </div>
          </div>

          {jobsFiltered.length === 0 ? (
            <EmptyState icon={Briefcase} title="Sem vagas para mostrar" description="Nenhuma vaga corresponde a este filtro." />
          ) : jobsView === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {jobsFiltered.map((j) => {
                const [label, cls] = JOB_STATUS[j.status] || ["—", "bg-slate-100 text-slate-600"];
                return (
                  <div key={j.id} data-testid={`admin-job-${j.id}`}
                    className="bg-white rounded-xl border border-black/5 overflow-hidden hover:border-amber-300 hover:shadow-md transition group flex flex-col">
                    <button type="button" onClick={() => openJobDetail(j.id)} className="relative block w-full h-32 sm:h-36 bg-slate-100 overflow-hidden">
                      <JobCoverImage job={j} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />
                      <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2">
                        <Badge className={`${cls} hover:${cls}`}>{label}</Badge>
                        {j.boost?.tier && (
                          <Badge className="bg-amber-500 text-white text-[10px] font-black uppercase shadow">
                            {j.boost.tier}
                          </Badge>
                        )}
                      </div>
                    </button>
                    <div className="p-3 flex-1 flex flex-col">
                      <button type="button" onClick={() => openJobDetail(j.id)} className="text-left">
                        <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 truncate">
                          {j.company_name}
                        </p>
                        <p className="font-heading font-bold tracking-tight text-slate-900 hover:text-amber-600 line-clamp-2 mt-0.5 text-[15px] leading-snug">
                          {j.title}
                        </p>
                        <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-1.5">
                          <MapPin className="w-3 h-3 shrink-0" />
                          <span className="truncate">{j.concelho}, {j.district}</span>
                          {j.source_type && j.source_type !== "internal" && (
                            <span className="ml-1 shrink-0 rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[9px] font-semibold uppercase">
                              {j.source_type}
                            </span>
                          )}
                        </p>
                      </button>
                      <AiModerationBadge job={j} onChanged={refresh} className="mt-2" />
                      <div className="flex flex-wrap items-center gap-1 mt-3 pt-3 border-t border-slate-100">
                        {j.status === "pending" && (
                          <>
                            <Button data-testid={`approve-job-${j.id}`} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-7 px-2" onClick={(e) => { e.stopPropagation(); moderate("jobs", j.id, "approve"); }}>
                              <Check className="w-3.5 h-3.5" />
                            </Button>
                            <Button data-testid={`reject-job-${j.id}`} size="sm" variant="destructive" className="rounded-full h-7 px-2" onClick={(e) => { e.stopPropagation(); moderate("jobs", j.id, "reject"); }}>
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                        <Button data-testid={`edit-job-${j.id}`} size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={(e) => { e.stopPropagation(); openEditJob(j); }}>
                          <Pencil className="w-3 h-3 mr-1" /> Editar
                        </Button>
                        {j.status === "active" && (
                          <Button data-testid={`deactivate-job-${j.id}`} size="sm" variant="outline" className="rounded-full h-7 text-xs text-slate-600" onClick={(e) => { e.stopPropagation(); deactivateJob(j); }}>
                            <Ban className="w-3 h-3" />
                          </Button>
                        )}
                        <Button data-testid={`delete-job-${j.id}`} size="sm" variant="ghost" className="rounded-full h-7 text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto" onClick={(e) => { e.stopPropagation(); deleteJob(j); }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {jobsFiltered.map((j) => {
                const [label, cls] = JOB_STATUS[j.status] || ["—", "bg-slate-100 text-slate-600"];
                return (
                  <div key={j.id} data-testid={`admin-job-${j.id}`}
                    className="bg-white rounded-xl border border-black/5 p-3 sm:p-4 flex flex-wrap items-center justify-between gap-3 hover:border-amber-300 hover:shadow-sm transition">
                    <button type="button" onClick={() => openJobDetail(j.id)} className="text-left flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-lg overflow-hidden bg-slate-100">
                        <JobCoverImage job={j} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-heading font-bold tracking-tighter text-slate-900 hover:text-amber-600 line-clamp-1">{j.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{j.company_name} · {j.concelho}, {j.district}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">fonte: {j.source_type || "internal"}</p>
                        <AiModerationBadge job={j} noAction className="mt-1" />
                      </div>
                    </button>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <Badge className={`${cls} hover:${cls}`}>{label}</Badge>
                      {j.boost?.tier && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{j.boost.tier}</Badge>}
                      {j.status === "pending" && (
                        <>
                          <Button data-testid={`approve-job-${j.id}`} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={(e) => { e.stopPropagation(); moderate("jobs", j.id, "approve"); }}>
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button data-testid={`reject-job-${j.id}`} size="sm" variant="destructive" className="rounded-full" onClick={(e) => { e.stopPropagation(); moderate("jobs", j.id, "reject"); }}>
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button data-testid={`edit-job-${j.id}`} size="sm" variant="outline" className="rounded-full" onClick={(e) => { e.stopPropagation(); openEditJob(j); }}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                      </Button>
                      {j.status === "active" && (
                        <Button data-testid={`deactivate-job-${j.id}`} size="sm" variant="outline" className="rounded-full text-slate-600" onClick={(e) => { e.stopPropagation(); deactivateJob(j); }}>
                          <Ban className="w-3.5 h-3.5 mr-1" /> Desativar
                        </Button>
                      )}
                      <Button data-testid={`delete-job-${j.id}`} size="sm" variant="ghost" className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); deleteJob(j); }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {jobsHasMore && (
            <div className="mt-5 text-center">
              <Button data-testid="admin-jobs-load-more" variant="outline" className="rounded-full" disabled={jobsLoadingMore} onClick={loadMoreJobs}>
                {jobsLoadingMore ? "A carregar…" : `Ver mais (${jobs.length.toLocaleString("pt-PT")} de ${jobsTotal.toLocaleString("pt-PT")})`}
              </Button>
            </div>
          )}
        </div>
      );
    }
    if (section === "companies") {
      if (selectedCompanyId) {
        return <AdminCompanyProfileSection companyId={selectedCompanyId} onBack={closeCompanyProfile} onOpenJob={openJobDetail} />;
      }
      return (
        <div>
          <div className="mb-4">
            <h2 className="font-heading font-black tracking-tighter text-2xl text-slate-900">Empresas</h2>
            <p className="text-sm text-slate-500">Aprova/rejeita empresas ou clica numa linha para o perfil completo (vagas, reviews, pagamentos).</p>
          </div>
          <PendingFilterChip active={onlyPending} count={pendingCompaniesCount} onToggle={() => setOnlyPending((v) => !v)} testId="filter-pending-companies" />
          <div className="relative flex-1 max-w-md mb-4">
            <input
              type="search"
              data-testid="admin-companies-search"
              value={companiesSearch}
              onChange={(e) => setCompaniesSearch(e.target.value)}
              placeholder="Procurar empresa por nome ou distrito…"
              className="w-full h-10 rounded-full border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400"
            />
          </div>
          <div className="space-y-3">
            {companiesShown.length === 0 ? (
              <EmptyState icon={Users} title="Sem empresas para mostrar" description={onlyPending ? "Não há empresas à espera de aprovação." : "Ainda não existem empresas registadas."} />
            ) : companiesShown.map((c) => (
              <div key={c.id} data-testid={`admin-company-${c.id}`} className="bg-white rounded-xl border border-black/5 p-5 flex flex-wrap items-center justify-between gap-3 hover:border-amber-300 transition-colors">
                <button type="button" onClick={() => openCompanyProfile(c.id)} className="text-left flex-1 min-w-0">
                  <p className="font-heading font-bold tracking-tighter text-slate-900 hover:text-amber-600">{c.company_name}</p>
                  <p className="text-sm text-slate-500">{c.district || "Sem distrito"} · registada em {new Date(c.created_at).toLocaleDateString("pt-PT")}</p>
                </button>
                <div className="flex items-center gap-2">
                  <Badge className={c.status === "approved" ? "bg-emerald-100 text-emerald-700" : c.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>
                    {c.status === "approved" ? "Aprovada" : c.status === "rejected" ? "Rejeitada" : "Pendente"}
                  </Badge>
                  {c.status === "pending" && (
                    <>
                      <Button data-testid={`approve-company-${c.id}`} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={(e) => { e.stopPropagation(); moderate("companies", c.id, "approve"); }}>
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button data-testid={`reject-company-${c.id}`} size="sm" variant="destructive" className="rounded-full" onClick={(e) => { e.stopPropagation(); moderate("companies", c.id, "reject"); }}>
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (section === "applications") {
      return <AdminApplicationsSection onOpenJob={openJobDetail} onOpenUser={openUserProfile} />;
    }
    if (section === "boosts") {
      return (
        <div>
          <div className="mb-4">
            <h2 className="font-heading font-black tracking-tighter text-2xl text-slate-900">Destaques comprados</h2>
            <p className="text-sm text-slate-500">Cada destaque adquirido via Stripe aparece aqui.</p>
          </div>
          {boosts.length === 0 ? (
            <EmptyState icon={Zap} title="Sem destaques comprados" description="Quando uma empresa comprar um destaque via Stripe, aparece aqui." />
          ) : (
            <div className="space-y-3">
              {boosts.map((b) => (
                <div key={b.id} data-testid={`admin-boost-${b.id}`} className="bg-white rounded-xl border border-black/5 p-5 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-heading font-bold tracking-tighter text-slate-900">{b.label} — {(b.amount / 100).toFixed(0)}€</p>
                    <p className="text-sm text-slate-500">Expira a {new Date(b.expires_at).toLocaleDateString("pt-PT")}</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">Pago</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (section === "emails") {
      return (
        <div>
          <div className="mb-4">
            <h2 className="font-heading font-black tracking-tighter text-2xl text-slate-900">Log de emails</h2>
            <p className="text-sm text-slate-500">Registo de todos os emails processados (welcomes, alertas, candidaturas).</p>
          </div>
          {!emailLog.resend_configured && (
            <div data-testid="emails-mocked-banner" className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-5">
              <p className="text-sm text-amber-800 font-semibold">Modo simulado (MOCKED)</p>
              <p className="text-xs text-amber-700 mt-1">Sem chave Resend configurada — os emails são registados aqui mas não enviados. Adiciona RESEND_API_KEY para ativar o envio real.</p>
            </div>
          )}
          {emailLog.emails.length === 0 ? (
            <EmptyState icon={Mail} title="Sem emails registados" description="Registos de boas-vindas, recibos, candidaturas e alertas aparecem aqui." />
          ) : (
            <div className="space-y-2">
              {emailLog.emails.map((e) => (
                <div key={e.id} data-testid={`email-log-${e.id}`} className="bg-white rounded-xl border border-black/5 p-4 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">{e.subject}</p>
                    <p className="text-xs text-slate-500">{e.to} · {e.kind} · {new Date(e.created_at).toLocaleString("pt-PT")}</p>
                  </div>
                  <Badge className={e.status === "sent" ? "bg-emerald-100 text-emerald-700" : e.status === "failed" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}>
                    {e.status === "sent" ? "Enviado" : e.status === "failed" ? "Falhou" : "Simulado"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    if (section === "connectors") {
      return (
        <div>
          <div className="mb-4">
            <h2 className="font-heading font-black tracking-tighter text-2xl text-slate-900">Conectores</h2>
            <p className="text-sm text-slate-500">Importações automáticas e dados de demonstração.</p>
          </div>
          <div className="space-y-6">
            <AdminGoogleJobsCard />
            <AdminAggregatorsSection />
            <div className="bg-white rounded-xl border border-black/5 p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-heading font-bold tracking-tighter text-slate-900 flex items-center gap-2">
                    <PlugZap className="w-5 h-5 text-emerald-600" /> IEFP — Importação automática
                  </p>
                  <p className="text-sm text-slate-500 mt-1">Vagas públicas do portal IEFP de todo o país — até 5.000 por importação. Corre automaticamente a cada 24 horas.</p>
                  <Badge data-testid="iefp-total-badge" className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 mt-2">
                    {importData.iefp_jobs_total} vagas IEFP na plataforma
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button data-testid="iefp-import-btn" className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={importing} onClick={runImport}>
                    <Download className="w-4 h-4 mr-1.5" /> {importing ? "A importar..." : "Importar agora"}
                  </Button>
                  <Button data-testid="iefp-refresh-btn" variant="outline" className="rounded-full" onClick={refresh}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {importData.runs.length > 0 && (
                <div className="mt-5 pt-4 border-t border-black/5 space-y-2">
                  {importData.runs.map((r) => (
                    <div key={r.id} data-testid={`import-run-${r.id}`} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="text-slate-500">{new Date(r.started_at).toLocaleString("pt-PT")} · {r.trigger === "auto" ? "automática" : "manual"}</span>
                      <span className="text-slate-700">
                        {r.available ? `${r.available.toLocaleString("pt-PT")} no IEFP · ` : ""}{r.found} lidas · <b className="text-emerald-700">{r.imported} importadas</b>
                        {r.updated ? ` · ${r.updated} já existiam` : ""} · {r.skipped} ignoradas
                        {r.flagged ? <> · <b className="text-amber-700">{r.flagged} retidas p/ IA</b></> : null}
                      </span>
                      <Badge className={r.status === "done" ? "bg-emerald-100 text-emerald-700" : r.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}>
                        {r.status === "done" ? "Concluída" : r.status === "failed" ? "Falhou" : "Em curso"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-xl border border-black/5 p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="max-w-2xl">
                  <p className="font-heading font-bold tracking-tighter text-slate-900 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" /> Dados de demonstração
                  </p>
                  <p className="text-sm text-slate-500 mt-1">Cria (ou repõe) um conjunto rico de registos de exemplo: 6 candidatos, 6 empresas, 21 vagas, 11 candidaturas, 14 reviews, 5 banners, CVs analisados, wallet, referrals e histórico Stripe.</p>
                  <p className="text-xs text-slate-400 mt-2">Password universal demo: <code className="bg-slate-100 px-1.5 py-0.5 rounded">Demo@2026</code>. Só apaga registos com marker <code className="bg-slate-100 px-1.5 py-0.5 rounded">_demo=True</code>.</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button data-testid="seed-populate-btn" className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={seeding} onClick={() => runSeed(false)}>
                    <Sparkles className="w-4 h-4 mr-1.5" /> {seeding ? "A criar..." : "Popular"}
                  </Button>
                  <Button data-testid="seed-purge-content-btn" variant="outline" className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800" disabled={seeding} onClick={runPurgeContent}>
                    <Trash2 className="w-4 h-4 mr-1.5" /> Apagar só conteúdos (manter contas)
                  </Button>
                  <Button data-testid="seed-reset-btn" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" disabled={seeding} onClick={() => setSeedResetOpen(true)}>
                    <Trash2 className="w-4 h-4 mr-1.5" /> Repor tudo
                  </Button>
                </div>
              </div>
              {seedResult?.created && Object.keys(seedResult.created).length > 0 && (
                <div data-testid="seed-summary" className="mt-5 pt-4 border-t border-black/5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                  {Object.entries(seedResult.created).map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-slate-50 px-3 py-2">
                      <p className="font-heading font-black tracking-tighter text-xl text-slate-900">{v}</p>
                      <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500 mt-0.5">{k.replace(/_/g, " ")}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    if (section === "ads") {
      return (
        <div>
          <div className="mb-4">
            <h2 className="font-heading font-black tracking-tighter text-2xl text-slate-900">Banners publicitários</h2>
            <p className="text-sm text-slate-500">Cria e gere banners nas várias zonas do site.</p>
          </div>
          <AdminAdsTab />
        </div>
      );
    }
    if (section === "keys") {
      return (
        <div>
          <div className="mb-4">
            <h2 className="font-heading font-black tracking-tighter text-2xl text-slate-900">Chaves API & integrações</h2>
            <p className="text-sm text-slate-500">Configura e testa as chaves de cada serviço externo em tempo real.</p>
          </div>
          <AdminApiKeysTab />
        </div>
      );
    }
    if (section === "legal") {
      return (
        <div>
          <div className="mb-4">
            <h2 className="font-heading font-black tracking-tighter text-2xl text-slate-900">Páginas legais</h2>
            <p className="text-sm text-slate-500">Termos, privacidade, cookies e outros documentos legais.</p>
          </div>
          <AdminLegalTab />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Navbar />
      <div className="flex-1 flex">
        <AdminSidebar
          current={section}
          onNavigate={(s) => navigateTo(s)}
          badges={{ pendingJobs: pendingJobsCount, pendingCompanies: pendingCompaniesCount }}
        />
        <main className="flex-1 min-w-0 overflow-x-hidden">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 mb-2 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />Administração
            </p>
            <h1 data-testid="admin-dashboard-title" className="font-heading font-black tracking-tighter text-3xl text-slate-900 mb-6 lg:sr-only">Painel Admin</h1>
            {renderSection()}
          </div>
        </main>
      </div>

      <Dialog open={seedResetOpen} onOpenChange={setSeedResetOpen}>
        <DialogContent data-testid="seed-reset-dialog">
          <DialogHeader>
            <DialogTitle>Repor dados de demonstração?</DialogTitle>
            <DialogDescription>
              Vai apagar <b>todos</b> os registos criados pelo seed demo (users, companies, jobs, applications, CVs, reviews, banners, etc. com marker <code>_demo=True</code>) e voltar a criar tudo do zero. Dados reais criados manualmente por utilizadores <b>não</b> são afetados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setSeedResetOpen(false)}>Cancelar</Button>
            <Button data-testid="seed-reset-confirm-btn" className="rounded-full bg-red-600 hover:bg-red-700 text-white" onClick={() => runSeed(true)}>
              <Trash2 className="w-4 h-4 mr-1.5" /> Sim, repor tudo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AdminJobFormDialog
        open={jobFormOpen}
        onClose={() => setJobFormOpen(false)}
        meta={meta}
        companies={companies}
        job={jobEditData}
        onSaved={refresh}
      />
    </div>
  );
}
